import { useCallback, useSyncExternalStore } from "react";

import { DASHBOARD_ALERT_TYPES, type DashboardAlertType } from "./dashboardAlertCatalog";

/**
 * How each alert shows: with its sound, silently, or not at all. Every alert starts with its sound; the
 * person picks otherwise in Settings → Avisos en el dashboard.
 */
export type DashboardAlertMode = "sound" | "silent" | "off";

export const DASHBOARD_ALERT_MODES: readonly DashboardAlertMode[] = ["sound", "silent", "off"];

/**
 * What the person chose: one master switch for every sound (to quiet them all without losing the choices
 * below, during a meeting say) and the alerts set to anything but the default. Remembered per browser, like
 * the theme, so the till's tablet and the office laptop can differ. Storing only what differs from the
 * default is what makes every alert, present or future, start with its sound.
 */
export interface DashboardAlertPreferences {
  sound: boolean;
  modes: Partial<Record<DashboardAlertType, Exclude<DashboardAlertMode, "sound">>>;
}

const STORAGE_KEY = "coloca:dashboard-alerts";
const DEFAULTS: DashboardAlertPreferences = { sound: true, modes: {} };
const KNOWN = new Set<string>(DASHBOARD_ALERT_TYPES);

/** Reads whatever is stored, keeping only what this build understands. Anything else is the defaults. */
export function parseDashboardAlertPreferences(raw: string | null): DashboardAlertPreferences {
  if (!raw) return DEFAULTS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return DEFAULTS;
    const { sound, modes } = parsed as { sound?: unknown; modes?: unknown };
    const kept: DashboardAlertPreferences["modes"] = {};
    if (modes && typeof modes === "object" && !Array.isArray(modes)) {
      for (const [type, mode] of Object.entries(modes)) {
        if (KNOWN.has(type) && (mode === "silent" || mode === "off")) kept[type as DashboardAlertType] = mode;
      }
    }
    return { sound: typeof sound === "boolean" ? sound : true, modes: kept };
  } catch {
    return DEFAULTS;
  }
}

function readStored(): DashboardAlertPreferences {
  try {
    return parseDashboardAlertPreferences(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULTS;
  }
}

let current = readStored();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

/** Reads storage again; true when it changed. The same object stays otherwise, as React expects of a snapshot. */
function refresh(): boolean {
  const next = readStored();
  if (JSON.stringify(next) === JSON.stringify(current)) return false;
  current = next;
  return true;
}

function onStorage(event: StorageEvent) {
  // `key` is null when another tab cleared the whole storage.
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  if (refresh()) notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Nobody listened to the other tabs until now: what they changed meanwhile is read first.
  if (listeners.size === 1) {
    window.addEventListener("storage", onStorage);
    refresh();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

function save(next: DashboardAlertPreferences) {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Without storage the choice still holds until the tab is reloaded.
  }
  notify();
}

/** The choice as it stands now, for code that runs when an event lands rather than on render. */
export function readDashboardAlertPreferences(): DashboardAlertPreferences {
  if (listeners.size === 0) refresh();
  return current;
}

export function dashboardAlertModeOf(
  prefs: DashboardAlertPreferences,
  type: DashboardAlertType,
): DashboardAlertMode {
  return prefs.modes[type] ?? "sound";
}

/** Whether an alert of this type plays its sound right now: its own mode and the master switch together. */
export function dashboardAlertPlaysSound(
  prefs: DashboardAlertPreferences,
  type: DashboardAlertType,
): boolean {
  return prefs.sound && dashboardAlertModeOf(prefs, type) === "sound";
}

/** Sets one alert's mode, outside React (the static QR's speaker). */
export function setDashboardAlertMode(type: DashboardAlertType, mode: DashboardAlertMode): void {
  const modes = { ...current.modes };
  if (mode === "sound") delete modes[type];
  else modes[type] = mode;
  save({ ...current, modes });
}

export function setDashboardAlertSound(sound: boolean): void {
  save({ ...current, sound });
}

export function useDashboardAlertPreferences() {
  const prefs = useSyncExternalStore(
    subscribe,
    () => current,
    () => DEFAULTS,
  );
  const setSound = useCallback((sound: boolean) => setDashboardAlertSound(sound), []);
  const setMode = useCallback(
    (type: DashboardAlertType, mode: DashboardAlertMode) => setDashboardAlertMode(type, mode),
    [],
  );
  return {
    sound: prefs.sound,
    modeOf: (type: DashboardAlertType) => dashboardAlertModeOf(prefs, type),
    playsSound: (type: DashboardAlertType) => dashboardAlertPlaysSound(prefs, type),
    setSound,
    setMode,
  };
}
